import { useEffect, useState } from "react";
import { UploadSimple, Trash, FileText, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  pending: { text: "Waiting for review", cls: "bg-orange-100 text-orange-800", icon: Clock },
  approved: { text: "Approved", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  rejected: { text: "Rejected", cls: "bg-red-100 text-red-800", icon: XCircle },
};
const WITH_NUMBER = { gstin: "GSTIN (15 characters)", fssai: "FSSAI number (14 digits)", mandi_licence: "Licence number" };

export default function Kyc() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({ doc_type: "id_proof", doc_number: "", file: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, d] = await Promise.all([api.get("/kyc/types"), api.get("/kyc/documents")]);
      setTypes(t.data);
      setDocs(d.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  if (user?.role === "admin") {
    return <div className="rounded-xl bg-white border border-stone-200 p-6 text-sm text-stone-600">Admin accounts do not need KYC. Review other people's papers under "KYC papers".</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error("Choose a file first");
    const body = new FormData();
    body.append("doc_type", form.doc_type);
    if (WITH_NUMBER[form.doc_type] && form.doc_number) body.append("doc_number", form.doc_number);
    body.append("file", form.file);
    setBusy(true);
    try {
      await api.post("/kyc/documents", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded. The India Mandi team will review it.");
      setForm({ doc_type: form.doc_type, doc_number: "", file: null });
      e.target.reset();
      load();
    } catch (ex) {
      toast.error(formatErr(ex?.response?.data?.detail));
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this paper?")) return;
    try { await api.delete(`/kyc/documents/${id}`); load(); } catch (ex) { toast.error(formatErr(ex?.response?.data?.detail)); }
  };

  const label = (key) => (types.find((t) => t.key === key)?.label || key).split(" (")[0];

  return (
    <div className="space-y-6" data-testid="kyc-page">
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Verification</div>
        <div className="font-display text-2xl font-bold text-stone-900">Your KYC papers</div>
        <div className="mt-2 flex items-center gap-2 text-sm">
          Status:
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${user?.verified ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>
            {user?.verified ? "verified" : user?.kyc_status || "pending"}
          </span>
        </div>
        <p className="text-sm text-stone-600 mt-2 max-w-2xl">
          Upload the papers that show who you are and what you trade in. The India Mandi team checks them and marks your account verified.
          PDF, JPG or PNG, up to 5 MB each. We keep only what you upload here.
        </p>
      </div>

      <form onSubmit={submit} className="rounded-xl bg-white border border-stone-200 p-5 grid sm:grid-cols-3 gap-4 items-end">
        <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Paper
          <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value, doc_number: "" })} className="mt-1 w-full px-3 py-2.5 rounded-md border border-stone-300 bg-white text-sm normal-case tracking-normal font-normal" data-testid="kyc-type">
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
        {WITH_NUMBER[form.doc_type] ? (
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500">{WITH_NUMBER[form.doc_type]}
            <input value={form.doc_number} onChange={(e) => setForm({ ...form, doc_number: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-md border border-stone-300 text-sm normal-case tracking-normal font-normal" data-testid="kyc-number" />
          </label>
        ) : <div />}
        <label className="text-xs font-bold uppercase tracking-widest text-stone-500">File
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="mt-1 w-full text-sm normal-case tracking-normal font-normal" data-testid="kyc-file" />
        </label>
        <div className="sm:col-span-3 flex justify-end">
          <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-emerald-900 hover:bg-emerald-800 text-white font-bold disabled:opacity-60" data-testid="kyc-submit">
            <UploadSimple size={16} weight="bold" /> {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </form>

      <div className="rounded-xl bg-white border border-stone-200 divide-y divide-stone-100">
        {docs === null && <div className="p-5"><div className="h-6 shimmer rounded" /></div>}
        {docs && docs.length === 0 && <div className="p-6 text-sm text-stone-500">You have not uploaded anything yet.</div>}
        {docs?.map((d) => {
          const st = STATUS[d.status] || STATUS.pending;
          return (
            <div key={d.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`kyc-doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={22} className="text-stone-500 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold text-stone-900">{label(d.doc_type)}{d.doc_number ? ` · ${d.doc_number}` : ""}</div>
                  <a href={`${API}/kyc/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="text-xs text-emerald-800 hover:underline truncate block">{d.filename}</a>
                  {d.status === "rejected" && d.reject_reason && <div className="text-xs text-red-700 mt-1">Reason: {d.reject_reason}</div>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded ${st.cls}`}><st.icon size={12} weight="bold" /> {st.text}</span>
                {d.status === "pending" && (
                  <button onClick={() => remove(d.id)} className="p-1.5 rounded text-stone-500 hover:text-red-700 hover:bg-red-50" title="Delete" data-testid={`kyc-delete-${d.id}`}><Trash size={16} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
